import { Button } from '@mui/material'
import styled from '@emotion/styled/macro'
import type { ThemeVariant, ThemeButtonShape } from './ThemeButton'
import theme from 'theme'

export const StyledButton = styled(Button)<{
  themevariant?: ThemeVariant
  noActive?: boolean | undefined
  font?: string
  shape?: ThemeButtonShape
}>`
  border: 1px solid ${theme.colors.cta};
  border-radius: ${theme.attributes.borderRadius.large};
  background: ${theme.colors.cta};
  display: flex;
  justify-content: center;
  align-items: center;
  padding: ${theme.spacing.sm}px 22px ${theme.spacing.sm}px 14px;
  gap: ${theme.spacing.xSmall}px;
  font-family: ${theme.typography.fontFamily.primary};
  font-weight: ${theme.typography.fontWeight.bold};
  font-size: ${theme.typography.fontSize.large};
  line-height: 20px;
  text-align: center;
  letter-spacing: 0.1px;
  color: ${theme.baseColors.primary[20]};
  height: 40px;
  text-transform: none;
  white-space: nowrap;

  &:hover {
    background: rgba(208, 188, 255, 0.12);
    color: ${theme.baseColors.primary[90]};
  }

  ${({ font }) =>
    font === 'big' &&
    `
     font-size: ${theme.typography.fontSize.xLarge};
     font-style: normal;
     font-weight: ${theme.typography.fontWeight.thin};
     line-height: normal;
  `};

  ${({ noActive }) =>
    !noActive &&
    `
      &:active,
      &:focus {
        background: rgba(208, 188, 255, 0.12);
        color: ${theme.baseColors.primary[90]};
      }
  `};

  ${({ themevariant, shape }) =>
    themevariant &&
    themevariant === 'primary' &&
    `
      color: ${shape === 'rectangular' ? theme.colors.surfaceContainerBackground : theme.baseColors.primary[20]};
      text-align: center;
      font-family: ${theme.typography.fontFamily.primary};
      font-size: ${shape === 'rectangular' ? theme.typography.fontSize.large : theme.typography.fontSize.xLarge};
      font-style: normal;
      font-weight: ${shape === 'rectangular' ? theme.typography.fontWeight.bold : theme.typography.fontWeight.thin};
      line-height: ${shape === 'rectangular' ? '22px' : 'normal'};

      svg {
        path {
          fill: ${theme.baseColors.primary[20]};
        }
      }
      
      &:hover {
        background: ${theme.baseColors.primary[70]};
        color: ${theme.baseColors.primary[20]};
        border-color: ${theme.baseColors.primary[30]};
        box-shadow: ${theme.attributes.boxShadow.quaternary};
        
        svg {
          path {
            fill: ${theme.baseColors.primary[20]};
          }
        }
      }
      
      &:active, &:focus {
        background: ${theme.colors.cta};
        color: ${theme.baseColors.primary[20]};
        border-color: ${theme.colors.white};
        
        svg {
          path {
            fill: ${theme.baseColors.primary[20]};
          }
        }
      }
      
      &.Mui-disabled {
        color: ${shape === 'rectangular' ? theme.colors.surfaceContainerBackground : theme.colors.btnSecondaryDisabledText};
        font-weight: ${shape === 'rectangular' ? theme.typography.fontWeight.bold : theme.typography.fontWeight.thin};
        background: ${shape === 'rectangular' ? '#946592' : theme.colors.btnTertiaryBg};
        border: none;

        svg {
          path {
            fill: ${theme.colors.btnSecondaryDisabledText};
          }
        }
      }

      html[data-theme='light'] & {
        background: ${theme.colors.cta};
        border-color: ${theme.colors.cta};
        color: ${theme.colors.surfaceContainerBackground};

        svg {
          path {
            fill: ${theme.colors.surfaceContainerBackground};
          }
        }

        &:hover {
          background: ${theme.colors.buttonHollowHover};
          border-color: ${theme.colors.buttonHollowHover};
          color: ${theme.colors.surfaceContainerBackground};

          svg {
            path {
              fill: ${theme.colors.surfaceContainerBackground};
            }
          }
        }

        /* :focus-visible, not :focus: a clicked button keeps focus and would stay in the pressed colour. */
        &:active,
        &:focus-visible {
          background: ${theme.colors.buttonHollowFocus};
          border-color: ${theme.colors.buttonHollowFocus};
          color: ${theme.colors.surfaceContainerBackground};

          svg {
            path {
              fill: ${theme.colors.surfaceContainerBackground};
            }
          }
        }

        &.Mui-disabled {
          background: ${theme.colors.cardFillTertiary};
          border-color: ${theme.colors.cardFillTertiary};
          color: ${theme.colors.btnSecondaryDisabledText};

          svg {
            path {
              fill: ${theme.colors.btnSecondaryDisabledText};
            }
          }
        }
      }
  `};

  ${({ themevariant, shape }) =>
    themevariant &&
    themevariant === 'secondary' &&
    `
      font-size: ${shape === 'rectangular' ? theme.typography.fontSize.large : theme.typography.fontSize.xLarge};
      font-weight: ${shape === 'rectangular' ? theme.typography.fontWeight.bold : theme.typography.fontWeight.thin};
      padding: ${
        shape === 'rectangular' ? `13px ${theme.spacing.medium}px` : `${theme.spacing.sm}px ${theme.spacing.small}px`
      };
      height: 40px;
      /* Rounded secondaries were pink-on-grey-border in the original dark theme; only rectangular ones were grey. */
      color: ${shape === 'rectangular' ? theme.colors.btnSecondaryText : theme.colors.legacyTextPrimary};
      background: transparent;
      border-color: ${shape === 'rectangular' ? theme.colors.btnSecondaryBorder : theme.colors.controlBorder};

      svg {
        path {
          fill: ${theme.colors.legacyTextPrimary};
        }
      }

      &:hover {
        border: 1px solid ${shape === 'rectangular' ? theme.colors.btnSecondaryBorder : theme.colors.controlBorder};
        background: ${theme.colors.legacySecondary30};
        color: ${shape === 'rectangular' ? theme.colors.btnSecondaryText : theme.colors.legacyTextPrimary};

        svg {
          path {
            fill: ${theme.colors.legacyTextPrimary};
          }
        }
      }

      &:active, &:focus {
        background: transparent;
        color: ${theme.colors.cta};
        border: 1px solid ${theme.colors.legacyWhite};

        svg {
          path {
            fill: ${theme.colors.legacyWhite};
          }
        }
      }

      &:disabled {
        border: 1px solid ${theme.colors.legacySurface30};
        background: ${theme.colors.btnSecondaryDisabledBg};
        color: ${theme.colors.btnSecondaryDisabledText};

        svg {
          path {
            fill: ${theme.colors.btnSecondaryDisabledText};
          }
        }
      }

      html[data-theme='light'] & {
        border-color: ${theme.colors.strokeSecondary};
        color: ${theme.colors.menuItemText};

        svg {
          path {
            fill: ${theme.colors.menuItemText};
          }
        }

        &:disabled {
          color: ${theme.colors.btnSecondaryDisabledText};
        }

        &:hover {
          border: 1px solid ${theme.colors.cardFillTertiary};
          background: ${theme.colors.cardFillTertiary};
          color: ${theme.colors.menuItemText};

          svg {
            path {
              fill: ${theme.colors.menuItemText};
            }
          }
        }
      }
  `};

  ${({ themevariant }) =>
    themevariant &&
    themevariant === 'tertiary' &&
    `
      color: ${theme.colors.cta};
      text-align: center;
      font-family: ${theme.typography.fontFamily.primary};
      font-size: ${theme.typography.fontSize.xLarge};
      font-style: normal;
      font-weight: ${theme.typography.fontWeight.thin};
      line-height: normal;
      background: transparent;
      border: none;

      svg {
        path {
          fill: ${theme.colors.cta};
        }
      }

      &:hover {
        background: ${theme.baseColors.secondary[30]};
        color: ${theme.colors.cta};

        svg {
          path {
            fill: ${theme.colors.cta};
          }
        }
      }

      &:active {
        background: ${theme.baseColors.primary[20]};
        color: ${theme.colors.white};
      }

      &:focus {
        background: transparent;
        color: ${theme.colors.cta};
        border: 1px solid ${theme.colors.white};
      }
      
      &:disabled {
        background: ${theme.baseColors.surface[50]};
        color: ${theme.baseColors.greyscale[50]};
        
        svg {
          path {
            fill: ${theme.baseColors.greyscale[50]};
          }
        }
      }
      
      &.Mui-disabled {
        svg {
          path {
            fill: ${theme.baseColors.greyscale[50]} !important;
          }
        }
      }

      html[data-theme='light'] & {
        &:hover {
          background: ${theme.colors.btnTertiaryBg};
          color: ${theme.colors.cta};

          svg {
            path {
              fill: ${theme.colors.cta};
            }
          }
        }

        &:active {
          background: ${theme.colors.btnTertiaryBg};
          color: ${theme.colors.cta};
        }

        &:focus {
          background: transparent;
          border: 1px solid transparent;
          color: ${theme.colors.cta};
        }

        &:disabled {
          background: ${theme.colors.cardFillTertiary};
          color: ${theme.colors.btnSecondaryDisabledText};

          svg {
            path {
              fill: ${theme.colors.btnSecondaryDisabledText};
            }
          }
        }
      }
  `};

  ${({ themevariant }) =>
    themevariant &&
    themevariant === 'quaternary' &&
    `
      color: ${theme.colors.cta};
      background: ${theme.colors.backgroundDarker};
      border: none;
      
      svg {
        path {
          fill: ${theme.colors.cta};
        }
      }
      
      &.Mui-disabled {
        background: rgba(227, 227, 227, 0.12);
        color: ${theme.colors.neutral};

        svg {
          path {
            fill: ${theme.colors.neutral};
            opacity: 0.38;
          }
        }
      }
      
      &:hover {
        background: rgba(208, 188, 255, 0.08);
        color: ${theme.colors.cta};
        
        svg {
          path {
            fill: ${theme.colors.cta};
          }
        }
      }
      
      &:active, &:focus {
        background: rgba(208, 188, 255, 0.12);
        color: ${theme.colors.cta};
      }
  `};

  ${({ themevariant }) =>
    themevariant &&
    themevariant === 'ghost' &&
    `
      color: ${theme.baseColors.primary[20]};
      background: ${theme.colors.cta};
      font-family: ${theme.typography.fontFamily.primary};
      font-size: ${theme.typography.fontSize.xLarge};
      font-weight: ${theme.typography.fontWeight.thin};
      border: none;
      border: 1px solid transparent;
      
      svg {
        path {
          fill: ${theme.baseColors.primary[20]};
        }
      }
      
     &:hover {
      background: ${theme.baseColors.primary[70]};
      color: ${theme.baseColors.primary[20]};
      border: 1px solid transparent;
     }
     
     &:focus {
      border: 1px solid ${theme.colors.white};
      background: ${theme.colors.cta};
     }
     
     &:active {
      background: ${theme.baseColors.primary[20]};
     }
     
     &:disabled {
      background: ${theme.baseColors.surface[50]};
      color: ${theme.baseColors.greyscale[50]};

      svg {
        path {
          fill: ${theme.baseColors.greyscale[50]};
        }
      }
     }

     html[data-theme='light'] & {
       background: ${theme.colors.cta};
       color: ${theme.colors.surfaceContainerBackground};

       svg {
         path {
           fill: ${theme.colors.surfaceContainerBackground};
         }
       }

       &:hover {
         background: ${theme.colors.buttonHollowHover};
         color: ${theme.colors.surfaceContainerBackground};
       }

       /* :focus-visible, not :focus: a clicked button keeps focus and would stay in the pressed colour. */
       &:focus-visible {
         border: 1px solid ${theme.colors.buttonHollowFocus};
         background: ${theme.colors.buttonHollowFocus};
         color: ${theme.colors.surfaceContainerBackground};
       }

       &:active {
         background: ${theme.colors.buttonHollowFocus};
       }

       &:disabled {
         background: ${theme.colors.btnTertiaryBg};
         color: ${theme.colors.btnSecondaryDisabledText};

         svg {
           path {
             fill: ${theme.colors.btnSecondaryDisabledText};
           }
         }
       }
     }
  `};

  ${({ themevariant }) =>
    themevariant &&
    themevariant === 'hollow' &&
    `
      background: transparent !important;
      padding: 0;
      outline: 0;
      border: 0;
      color: ${theme.colors.cta};
      font-family: ${theme.typography.fontFamily.primary};
      font-size: ${theme.typography.fontSize.large};
      font-style: normal;
      font-weight: ${theme.typography.fontWeight.extraThick};
      line-height: 22px;
      width: fit-content !important;
      height: fit-content !important;
      
      &:hover {
        color: #c477c1;
        
        svg {
          path {
            stroke: #c477c1;
          }
        }
      }
      
      &:focus {
        color: ${theme.colors.cta};
      }

      &.Mui-disabled, &:disabled {
        color: ${theme.baseColors.greyscale[50]};
      }
  `};

  ${({ shape }) =>
    shape === 'rectangular' &&
    `
      border-radius: ${theme.attributes.borderRadius.tertiary};
      height: 48px;
      padding: 13px 24px;
  `};
`

export const disabledStyles = {
  secondary: {
    textTransform: 'none !important',
    border: 'none !important',
    background: `${theme.colors.btnTertiaryBg} !important`,
    color: `${theme.colors.btnSecondaryDisabledText} !important`,
    cursor: 'default',
    svg: { path: { fill: `${theme.colors.btnSecondaryDisabledText} !important` } }
  }
}
